import type { AiActionType, AiActionPayloadMap } from "./actions";
import { getLinkHost } from "../utils/taskLinkUtils";

export type AiDescriptorTranslate = (key: string, opts?: Record<string, unknown>) => string;

export abstract class AiActionDescriptor<T extends AiActionType = AiActionType> {
  abstract readonly type: T;
  abstract formatDetail(payload: Partial<AiActionPayloadMap[T]>, t: AiDescriptorTranslate): string;
  /**
   * Everything the change will do, untruncated, one line per part, for the card's tooltip.
   * Defaults to the short detail; types whose detail is cut short override it.
   */
  formatChange(payload: Partial<AiActionPayloadMap[T]>, t: AiDescriptorTranslate): string[] {
    const detail = this.formatDetail(payload, t);
    return detail ? [detail] : [];
  }
}

class SetDueDateDescriptor extends AiActionDescriptor<"setDueDate"> {
  readonly type = "setDueDate" as const;
  formatDetail(p: Partial<AiActionPayloadMap["setDueDate"]>, t: AiDescriptorTranslate) {
    return p.dueDate ? t("ai.actions.setDueDate.detail", { dueDate: p.dueDate }) : "";
  }
}

class ScheduleTaskDescriptor extends AiActionDescriptor<"scheduleTask"> {
  readonly type = "scheduleTask" as const;
  formatDetail(p: Partial<AiActionPayloadMap["scheduleTask"]>, t: AiDescriptorTranslate) {
    return p.scheduledDate
      ? t("ai.actions.scheduleTask.detail", { scheduledDate: p.scheduledDate })
      : "";
  }
}

class AddTagsDescriptor extends AiActionDescriptor<"addTags"> {
  readonly type = "addTags" as const;
  formatDetail(p: Partial<AiActionPayloadMap["addTags"]>, _t: AiDescriptorTranslate) {
    return Array.isArray(p.tags) ? p.tags.join(", ") : "";
  }
}

class UpdateNotesDescriptor extends AiActionDescriptor<"updateNotes"> {
  readonly type = "updateNotes" as const;
  formatDetail(p: Partial<AiActionPayloadMap["updateNotes"]>, _t: AiDescriptorTranslate) {
    if (!p.notes) return "";
    return p.notes.length > 80 ? p.notes.slice(0, 80) + "…" : p.notes;
  }
  formatChange(p: Partial<AiActionPayloadMap["updateNotes"]>, _t: AiDescriptorTranslate) {
    return p.notes ? [p.notes] : [];
  }
}

class CreateSubtasksDescriptor extends AiActionDescriptor<"createSubtasks"> {
  readonly type = "createSubtasks" as const;
  formatDetail(p: Partial<AiActionPayloadMap["createSubtasks"]>, t: AiDescriptorTranslate) {
    if (!Array.isArray(p.titles)) return "";
    const titles = p.titles.slice(0, 3);
    const rest = p.titles.length - 3;
    if (rest > 0) return `${titles.join(", ")} ${t("ai.actions.createSubtasks.moreItems", { count: rest })}`;
    return titles.join(", ");
  }
  formatChange(p: Partial<AiActionPayloadMap["createSubtasks"]>, _t: AiDescriptorTranslate) {
    return Array.isArray(p.titles) ? p.titles.map((title) => `• ${title}`) : [];
  }
}

/** The link's name (else its site), so the card reads "Link → Finance chat" and the tooltip has the whole URL. */
class AddLinkDescriptor extends AiActionDescriptor<"addLink"> {
  readonly type = "addLink" as const;
  formatDetail(p: Partial<AiActionPayloadMap["addLink"]>, t: AiDescriptorTranslate) {
    if (!p.url) return "";
    return t("ai.actions.addLink.detail", { link: p.title?.trim() || getLinkHost(p.url) });
  }
  formatChange(p: Partial<AiActionPayloadMap["addLink"]>, t: AiDescriptorTranslate) {
    const lines: string[] = [];
    if (p.title?.trim()) lines.push(t("ai.actions.addLink.titleLine", { title: p.title.trim() }));
    if (p.url) lines.push(t("ai.actions.addLink.urlLine", { url: p.url }));
    return lines;
  }
}

/** The due date if one was given, then the quoted words that show the commitment. */
class CreateTaskDescriptor extends AiActionDescriptor<"createTask"> {
  readonly type = "createTask" as const;
  formatDetail(p: Partial<AiActionPayloadMap["createTask"]>, t: AiDescriptorTranslate) {
    const parts: string[] = [];
    if (p.dueDate) parts.push(t("ai.actions.createTask.due", { dueDate: p.dueDate }));
    if (p.evidence) {
      const quote = p.evidence.length > 80 ? p.evidence.slice(0, 80) + "…" : p.evidence;
      parts.push(`“${quote}”`);
    }
    return parts.join(" · ");
  }
  /** The title, due date, the whole quote and where it came from. */
  formatChange(p: Partial<AiActionPayloadMap["createTask"]>, t: AiDescriptorTranslate) {
    const lines: string[] = [];
    if (p.title) lines.push(t("ai.actions.createTask.titleLine", { title: p.title }));
    if (p.dueDate) lines.push(t("ai.actions.createTask.due", { dueDate: p.dueDate }));
    if (p.evidence) lines.push(`“${p.evidence}”`);
    if (p.sourceUrl) {
      lines.push(
        p.sourceTitle?.trim()
          ? t("ai.actions.createTask.sourceNamed", { title: p.sourceTitle.trim(), url: p.sourceUrl })
          : t("ai.actions.createTask.source", { url: p.sourceUrl }),
      );
    }
    return lines;
  }
}

/** The label already names the column and task; the payload only holds ids. */
class NoDetailDescriptor<T extends "moveToStatus" | "markDone" | "archiveTask"> extends AiActionDescriptor<T> {
  constructor(readonly type: T) {
    super();
  }
  formatDetail() {
    return "";
  }
}

export const AI_ACTION_DESCRIPTORS: { [T in AiActionType]: AiActionDescriptor<T> } = {
  setDueDate: new SetDueDateDescriptor(),
  scheduleTask: new ScheduleTaskDescriptor(),
  addTags: new AddTagsDescriptor(),
  updateNotes: new UpdateNotesDescriptor(),
  createSubtasks: new CreateSubtasksDescriptor(),
  moveToStatus: new NoDetailDescriptor("moveToStatus"),
  markDone: new NoDetailDescriptor("markDone"),
  archiveTask: new NoDetailDescriptor("archiveTask"),
  addLink: new AddLinkDescriptor(),
  createTask: new CreateTaskDescriptor(),
};
